import React from 'react';
import type { NextPageWithLayout, GetServerSideProps } from 'next';
import { Op, OrderBy } from 'contensis-delivery-api';
import { useRouter } from 'next/router';

import contensis from 'Common/api/contensis';
import cmsParser from 'Common/util/cmsParser';
import setCacheHeaders from 'Common/func/setCacheHeaders';
import { excludeMetadata } from 'Common/util/contensis/api';

import MainLayout from 'Layout/Main';
import Metadata from 'Components/Metadata';
import Template, { datamap, PageData } from 'Templates/MediaHub/Listing';

const MediaHubListing: NextPageWithLayout<PageData> = ({ entry, ...rest }) => {
    const router = useRouter();
    const metadata = cmsParser.metadata(entry);

    return (
        <>
            <Metadata
                {...metadata}
                canonical={router.query.page !== '1' ? router.asPath : entry.sys.uri}
                title={
                    metadata.title +
                    (router.query.page && router.query.page !== '1'
                        ? ` - page ${router.query.page}`
                        : '')
                }
            />
            <Template {...datamap({ entry, ...rest })} />
        </>
    );
};

MediaHubListing.getLayout = (page, props) => {
    return <MainLayout {...props}>{page}</MainLayout>;
};

export const getServerSideProps: GetServerSideProps<any> = async ({
    params,
    preview,
    res,
    query
}) => {
    const { fetch, surrogateKeys } = contensis({ preview, fetchSurrogateKeys: true });
    const { type } = params as { type: string };

    const { data, error } = await fetch(async (client, search) => {
        const resPage = await client.nodes.get({
            path: `/media-hub/${type}`,
            entryLinkDepth: 2,
            entryFields: ['*', ...excludeMetadata]
        });

        if (!resPage.entry) {
            return { error: true };
        }

        const entry = resPage.entry;
        const listingContentTypes = entry.listingContentType;

        // Function to parse filter query
        const parseFilterQuery = (query: string | string[]): string[] => {
            if (typeof query === 'string') {
                return query.split(',').flat();
            }
            return [];
        };

        // Parse the three URL parameters
        const querySubject = parseFilterQuery(query.subjectArea || []);
        const queryStoryType = parseFilterQuery(query.storyType || []);
        const queryContentTypes =
            parseFilterQuery(query.mediaType || []).length > 0
                ? parseFilterQuery(query.mediaType || [])
                : listingContentTypes;

        // Create Contensis ops from the parsed parameters
        const subjectQuery = [Op.in('subjectArea', ...[...querySubject])];
        const storyTypeQuery = [Op.in('storyClassification', ...[...queryStoryType])];

        // Create Contensis op from the search term
        const searchTerm = query.search || null;
        const searchTermQueries = searchTerm
            ? [
                  Op.or(
                      Op.freeText('title', searchTerm).weight(100),
                      Op.freeText('description', searchTerm).weight(75),
                      Op.contains('keywords', searchTerm).weight(50),
                      Op.freeText('hook', searchTerm).weight(25)
                  )
              ]
            : [];

        let listingQuery;
        // If search page with multiple content types else normal listing page
        if (queryContentTypes.length > 1 && queryContentTypes.includes('event')) {
            listingQuery = [
                Op.or(
                    Op.and(
                        Op.in('sys.contentTypeId', ...queryContentTypes),
                        Op.not(Op.equalTo('sys.contentTypeId', 'event'))
                    ),
                    // Separate events from others to filter those with past dates
                    Op.and(
                        Op.equalTo('sys.contentTypeId', 'event'),
                        Op.greaterThan('eventDate.to', new Date())
                    )
                ),
                Op.exists('customTags', false),
                Op.or(
                    Op.exists('isIndependentBlog', false),
                    Op.equalTo('isIndependentBlog', false)
                ),
                Op.exists('sys.slug', true),
                ...subjectQuery,
                ...storyTypeQuery,
                ...searchTermQueries
            ];
        } else {
            listingQuery = [
                Op.in('sys.contentTypeId', ...queryContentTypes),
                Op.exists('customTags', false),
                Op.or(
                    Op.exists('isIndependentBlog', false),
                    Op.equalTo('isIndependentBlog', false)
                ),
                Op.exists('sys.slug', true),
                ...subjectQuery,
                ...storyTypeQuery,
                ...searchTermQueries
            ];

            // If events listing add date filter
            queryContentTypes.includes('event') &&
                listingQuery.push(Op.greaterThan('eventDate.to', new Date()));
        }

        const resListing = await search(listingQuery, {
            pageSize: 15,
            pageIndex: parseInt(query.page as string) - 1 || 0,
            linkDepth: 1,
            orderBy: !searchTerm
                ? queryContentTypes.length === 1 && queryContentTypes.includes('event')
                    ? OrderBy.asc('eventDate.from')
                    : OrderBy.desc('publishDate')
                : undefined,
            fields: [
                'sys',
                'title',
                'description',
                'heroImage',
                'videoThumbnail',
                'subjectArea',
                'storyClassification',
                'basicLocation',
                'publishDate',
                'eventDate.from',
                'length',
                ...excludeMetadata
            ]
        });

        const ancestors = await client.nodes.getAncestors({
            id: resPage.id,
            entryFields: ['title']
        });

        const subjectsTaxonomy = await client.taxonomy.getNodeByKey({
            key: '0/1259/1638',
            childDepth: 1
        });

        const storyClassificationTaxonomy = await client.taxonomy.getNodeByKey({
            key: '0/1259/1262/1386',
            childDepth: 1
        });

        return {
            entry,
            ancestors,
            subjectsTaxonomy,
            storyClassificationTaxonomy,
            listingContentTypes,
            activeFilterQueries: {
                subjectArea: querySubject,
                storyType: queryStoryType,
                mediaType: listingContentTypes.length > 1 ? queryContentTypes : []
            },
            activeSearchTerm: searchTerm,
            listing: resListing
        };
    });

    if (error) {
        return {
            notFound: true
        };
    } else {
        setCacheHeaders(res, surrogateKeys);

        return {
            props: data
        };
    }
};

export default MediaHubListing;
